// Canvas adapter: the physical paint engine behind the existing Paint Tool.
//
// Implements the same interface as the original oil paint system
// (oilPaintGpu.js), so canvasGpuCore, the drawing handlers, layer actions,
// undo snapshots and document persistence work unchanged:
//   strokes      drawStroke/beginStroke/endStroke -> GPU engine (one per paint layer)
//   rendering    renderLayer composites the lit layer into the WebGL compositor;
//                renderSurface draws the document's paper first
//   undo         captureLayerSnapshot/restoreLayerSnapshot -> GPU buffer copies
//   saving       exportLayerState/importLayerState -> sparse physical state
//                (LayerStateCodec) in the document's paintLayerStates
// Layers saved by the original oil system are delegated to it (created on
// demand), so existing artwork keeps rendering and saving as before.
//
// Simulation runs on the GPU. Each lit layer is rendered on the GPU into an
// offscreen WebGPU canvas and handed to WebGL as an ImageBitmap (GPU to GPU);
// no simulation data is read back while painting. Readback happens only for
// saving (debounced after strokes) and for 64-byte activity counters.

import { createGpuPaintEngine, warmPaintPipelines } from '../gpu/WebGPUPaintEngine.js';
import { paperSpectrum, upsampleLinearRgb } from '../pigments/PigmentLibrary.js';
import { hexToSrgb, srgbToLinear } from '../pigments/ColourConversion.js';
import { createSurface, physicalSurfaceOf } from '../surfaces/PaperModel.js';
import { normaliseSurfaceSettings, surfaceStructureKey } from '../surfaces/SurfaceLibrary.js';
import { decodeLayerState, resamplePaintState, LAYER_STATE_VERSION } from './LayerStateCodec.js';
import { DEFAULT_INPUT } from '../input/StrokeInput.js';
import { paintActivity } from './paintActivity.js';
import { createPaintState } from '../core/PaintState.js';

// Document pixels per simulation cell. The engine's physical constants are per
// cell and were tuned in the Paint Lab, which simulates 480x320 cells shown at
// 2x; Canvas uses the same cell size so brushes, flow distances and paper grain
// keep the Lab's proportions (a 70 px tool is a 17.5-cell brush, as in the Lab).
export const DOC_PIXELS_PER_CELL = 2;
// Simulation cells per layer; larger documents simulate at reduced resolution.
export const MAX_SIM_CELLS = 1300000;
// GPU memory kept for undo snapshots before the oldest are released.
export const SNAPSHOT_BUDGET_BYTES = 1024 * 1024 * 1024;
const SURFACE_SEED = 7;

const QUAD_VS = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;
// Premultiplied layer image (coverage in its alpha), or an opaque image (the
// paper), top-down, composited with layer opacity.
const LAYER_FS = `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_colour;
uniform sampler2D u_alpha;
uniform float u_opacity;
uniform float u_opaque;
uniform float u_alphaInColour;
void main() {
  vec2 uv = vec2(v_uv.x, 1.0 - v_uv.y);
  vec4 t = texture2D(u_colour, uv);
  float a = u_opaque > 0.5 ? 1.0 : (u_alphaInColour > 0.5 ? t.a : texture2D(u_alpha, uv).r);
  gl_FragColor = vec4(t.rgb, a) * u_opacity;
}`;

function compile(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) || 'shader');
  return shader;
}

function hashString(text) {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return (h >>> 0) % 100000 + 1;
}

// Maps the existing Paint Tool settings (plus its physical section) to an engine tool.
export function toolFromPaintSettings(settings = {}, scale = 1, strokeId = '') {
  const physical = settings.physical && typeof settings.physical === 'object' ? settings.physical : {};
  const knife = String(settings.tool || '').toLowerCase() === 'knife';
  const pigment = physical.pigment && physical.pigment !== 'picker' ? physical.pigment : (settings.paintColor || '#d52b24');
  // As in the Lab, "mix" is the second pigment's share of the brush load.
  const mix = Number.isFinite(Number(physical.mix)) ? Math.max(0, Math.min(1, Number(physical.mix))) : 0.5;
  const pigments = [{ pigment, fraction: physical.secondPigment ? 1 - mix : 1 }];
  if (physical.secondPigment) pigments.push({ pigment: physical.secondPigment, fraction: mix });
  return {
    brush: knife ? 'palette_knife' : (physical.brush || 'filbert_hog'),
    radius: Math.max(1, ((Number(settings.toolSize) || 70) / 2) * scale),
    medium: Number.isFinite(Number(physical.medium)) ? Number(physical.medium) : 0.84,
    mediumOverrides: {
      waterScale: optionalNumber(physical.waterScale),
      thicknessScale: optionalNumber(physical.thicknessScale),
      pigmentScale: optionalNumber(physical.pigmentScale),
      fluidityScale: optionalNumber(physical.fluidityScale),
      dryingScale: optionalNumber(physical.dryingScale),
      pickupScale: optionalNumber(physical.pickupScale),
    },
    pigments,
    loading: Number.isFinite(Number(physical.loading)) ? Number(physical.loading) : 1,
    rotationMode: physical.rotationMode || 'direction',
    seed: hashString(String(strokeId || 'stroke')),
  };
}

function optionalNumber(value) {
  return value === undefined || value === null || value === '' || !Number.isFinite(Number(value)) ? undefined : Number(value);
}

// Environment as the Lab sets it: humidity, and the drying multiplier applied
// to both paint-body drying and water evaporation.
export function environmentFromPaintSettings(settings = {}) {
  const physical = settings.physical && typeof settings.physical === 'object' ? settings.physical : {};
  const drying = optionalNumber(physical.dryingScale) ?? 1;
  return { humidity: optionalNumber(physical.humidity) ?? 0.4, dryingScale: drying, evaporationScale: drying };
}

// Canvas points -> engine samples with the Lab's input semantics
// (input/StrokeInput.js): pen pressure through the same response curve, a
// simulated taper for mice (which report no real pressure), pen tilt and
// twist. `state` belongs to one stroke; `time` is supplied by the caller.
export function paintSampleFromPoint(point, settings, state, scale, time) {
  const basePressure = Math.max(0.05, Math.min(1, (Number(settings.brushPressure) || 100) / 100));
  const x = Number(point.x) * scale;
  const y = Number(point.y) * scale;
  const pointerType = String(point.pointerType || state.pointerType || 'mouse').toLowerCase();
  state.pointerType = pointerType;
  const raw = Number(point.inputPressure ?? point.pressure);
  let pressure;
  let simulated = false;
  if ((pointerType === 'pen' || pointerType === 'touch') && raw > 0) {
    pressure = Math.pow(Math.max(DEFAULT_INPUT.minimumPressure, Math.min(1, raw)), DEFAULT_INPUT.pressureCurve);
  } else {
    simulated = true;
    const ramp = Math.min(1, (state.travelled + 1) / DEFAULT_INPUT.mouseTaperDistance);
    pressure = 0.25 + 0.5 * Math.sqrt(ramp);
  }
  const sample = {
    x,
    y,
    pressure: pressure * basePressure,
    tiltX: Number(point.tiltX) || 0,
    tiltY: Number(point.tiltY) || 0,
    twist: Number(point.twist) || 0,
    time,
    simulated,
  };
  // As in StrokeInput, the taper uses the distance travelled before this point.
  if (state.last) state.travelled += Math.hypot(x - state.last.x, y - state.last.y);
  state.last = sample;
  return sample;
}

export function createPhysicalPaintSystem(gl, initialWidth, initialHeight, options = {}) {
  const { gpu } = options;
  const device = gpu.device;
  let docWidth = Math.max(1, Math.floor(initialWidth));
  let docHeight = Math.max(1, Math.floor(initialHeight));
  let scale = 1;
  let simWidth = docWidth;
  let simHeight = docHeight;
  const layers = new Map(); // id -> { engine, colourTex, needsUpload, containsPaint, wet, cache, cacheDirty }
  const sessions = new Map();
  let legacy = null;
  let destroyed = false;
  let surfaceSettings = normaliseSurfaceSettings(options.surface);
  let surfaceVisual = null;
  let surfacePhysics = null;
  let surfaceKey = '';
  let paperTex = null;
  let paperDirty = true;
  let light = { azimuth: 315, elevation: 35, specular: 0.6, view: 'shaded' };
  let timeScale = 1;
  let snapshotBytes = 0;
  const snapshots = [];

  // ---------------------------------------------------------------- sizes and surfaces
  const computeSizes = () => {
    const cells = (docWidth * docHeight) / (DOC_PIXELS_PER_CELL * DOC_PIXELS_PER_CELL);
    scale = (1 / DOC_PIXELS_PER_CELL) * (cells > MAX_SIM_CELLS ? Math.sqrt(MAX_SIM_CELLS / cells) : 1);
    simWidth = Math.max(8, Math.round(docWidth * scale));
    simHeight = Math.max(8, Math.round(docHeight * scale));
  };
  computeSizes();

  const rebuildSurfaces = () => {
    surfaceVisual = createSurface(simWidth, simHeight, surfaceSettings.preset, SURFACE_SEED, surfaceSettings);
    surfacePhysics = physicalSurfaceOf(surfaceVisual, surfaceSettings.mode, surfaceSettings.physicalStrength);
    surfaceKey = surfaceStructureKey(surfaceSettings);
  };
  rebuildSurfaces();

  // Paint is simulated over the paper's own reflectance spectrum, as in the
  // Lab (Kubelka-Munk over the real paper, not over white plus an alpha blend).
  const paperSubstrate = () => (surfaceSettings.paperColor
    ? upsampleLinearRgb(hexToSrgb(surfaceSettings.paperColor).map(srgbToLinear))
    : paperSpectrum(surfaceVisual.preset.tone ?? 0.86, surfaceVisual.preset.warmth ?? 0.04));
  let substrate = paperSubstrate();

  const displayOptions = () => ({
    // Same visual relief as the Lab (full strength); the user's relief and
    // visibility controls scale it.
    relief: (surfaceSettings.visibility ?? 1) * (surfaceSettings.relief ?? 1),
    sheen: surfacePhysics.preset.sheen,
  });

  const applySurfaceToEngine = (engine) => {
    engine.setPhysicsSurface(surfacePhysics);
    engine.setDisplaySurface(surfaceVisual.heightField, displayOptions());
    engine.setSubstrate(substrate);
  };

  // Changes the document surface. Existing paint is kept as it is; the new
  // physics applies to everything that happens from now on.
  const setSurfaceSettings = (next, { silent = false } = {}) => {
    const normalised = normaliseSurfaceSettings({ ...surfaceSettings, ...next });
    const structural = surfaceStructureKey(normalised) !== surfaceKey;
    const visual = JSON.stringify(normalised) !== JSON.stringify(surfaceSettings);
    surfaceSettings = normalised;
    if (structural) rebuildSurfaces();
    const nextSubstrate = paperSubstrate();
    const substrateChanged = nextSubstrate.some((v, b) => Math.abs(v - substrate[b]) > 1e-7);
    substrate = nextSubstrate;
    if (structural || visual) {
      for (const layer of layers.values()) {
        if (structural) applySurfaceToEngine(layer.engine);
        else layer.engine.setDisplaySurface(surfaceVisual.heightField, displayOptions());
        if (substrateChanged) layer.engine.setSubstrate(substrate);
        layer.needsUpload = true;
        layer.cacheDirty = true;
      }
      if (surfaceEngine && !layers.has(surfaceEngine.__layerId)) applySurfaceToEngine(surfaceEngine);
      paperDirty = true;
      if (!silent && typeof options.onSurfaceChange === 'function') options.onSurfaceChange(surfaceSettings);
      request();
    }
  };

  // ---------------------------------------------------------------- WebGL output
  const program = gl.createProgram();
  gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, QUAD_VS));
  gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, LAYER_FS));
  gl.linkProgram(program);
  const quad = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, quad);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
  const loc = {
    position: gl.getAttribLocation(program, 'a_position'),
    colour: gl.getUniformLocation(program, 'u_colour'),
    alpha: gl.getUniformLocation(program, 'u_alpha'),
    opacity: gl.getUniformLocation(program, 'u_opacity'),
    opaque: gl.getUniformLocation(program, 'u_opaque'),
    alphaInColour: gl.getUniformLocation(program, 'u_alphaInColour'),
  };
  const makeTexture = () => {
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);
    return tex;
  };
  const drawQuad = (colourTex, alphaTex, opacity, opaque, framebuffer, w, h) => {
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.viewport(0, 0, w, h);
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.enableVertexAttribArray(loc.position);
    gl.vertexAttribPointer(loc.position, 2, gl.FLOAT, false, 0, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, colourTex);
    gl.uniform1i(loc.colour, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, alphaTex || colourTex);
    gl.uniform1i(loc.alpha, 1);
    gl.uniform1f(loc.opacity, opacity);
    gl.uniform1f(loc.opaque, opaque ? 1 : 0);
    gl.uniform1f(loc.alphaInColour, !opaque && !alphaTex ? 1 : 0);
    if (opaque && opacity >= 1) gl.disable(gl.BLEND);
    else {
      gl.enable(gl.BLEND);
      gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    }
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(loc.position);
    gl.disable(gl.BLEND);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, null);
  };

  // Offscreen WebGPU canvases render the paper (opaque) and every layer
  // (premultiplied, coverage in alpha); each pass becomes a bitmap uploaded
  // to WebGL, GPU to GPU.
  let offscreen = null;
  const ensureOffscreen = () => {
    if (offscreen && offscreen.opaque.canvas.width === docWidth && offscreen.opaque.canvas.height === docHeight) return;
    const make = (alphaMode) => {
      const canvas = new OffscreenCanvas(docWidth, docHeight);
      const context = canvas.getContext('webgpu');
      context.configure({ device, format: 'rgba8unorm', alphaMode });
      return { canvas, context };
    };
    offscreen = { opaque: make('opaque'), premultiplied: make('premultiplied') };
  };
  const renderPass = (engine, mode, target) => {
    ensureOffscreen();
    const out = mode === 1 ? offscreen.premultiplied : offscreen.opaque;
    engine.renderToView(out.context.getCurrentTexture().createView(), 'rgba8unorm', { ...light, paperRelief: 1 }, mode);
    const bitmap = out.canvas.transferToImageBitmap();
    gl.bindTexture(gl.TEXTURE_2D, target);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    // Keep the layer premultiplied as rendered (no unpremultiply round trip).
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, mode === 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.bindTexture(gl.TEXTURE_2D, null);
    bitmap.close();
  };

  // ---------------------------------------------------------------- layers
  let surfaceEngine = null;
  const createEngine = (id) => {
    const engine = createGpuPaintEngine(gpu, { width: simWidth, height: simHeight, surfaceData: surfacePhysics, substrate, seed: SURFACE_SEED });
    engine.__layerId = id;
    engine.scheduler.maxStepsPerFrame = 4;
    engine.scheduler.timeScale = timeScale;
    applySurfaceToEngine(engine);
    return engine;
  };
  const physicalLayer = (id) => {
    const key = String(id || '').trim();
    if (!key) return null;
    let layer = layers.get(key);
    if (!layer) {
      layer = { id: key, engine: createEngine(key), colourTex: makeTexture(), needsUpload: true, containsPaint: false, wet: false, cache: null, cacheDirty: false, lastActivityCheck: 0 };
      layers.set(key, layer);
    }
    return layer;
  };
  const dropLayer = (key) => {
    const layer = layers.get(key);
    if (!layer) return;
    layer.engine.destroy();
    gl.deleteTexture(layer.colourTex);
    layers.delete(key);
  };
  const ensureLegacy = () => {
    if (!legacy && typeof options.createLegacySystem === 'function') legacy = options.createLegacySystem();
    return legacy;
  };

  const request = () => {
    if (typeof options.requestRender === 'function') options.requestRender();
  };

  // ---------------------------------------------------------------- simulation clock
  // Wet layers keep simulating at the correct simulated rate; a 64-byte GPU
  // counter readback tells us when a layer has gone completely still.
  let lastTick = 0;
  let raf = 0;
  const tick = (now) => {
    raf = 0;
    if (destroyed) return;
    const dt = lastTick ? Math.min(0.25, (now - lastTick) / 1000) : 0;
    lastTick = now;
    let anyWet = false;
    for (const layer of layers.values()) {
      if (!layer.wet) continue;
      anyWet = true;
      layer.engine.advance(dt);
      layer.needsUpload = true;
      layer.cacheDirty = true;
      if (now - layer.lastActivityCheck > 500 && !layer.checking) {
        layer.checking = true;
        layer.lastActivityCheck = now;
        layer.engine.readCounters().then((c) => {
          layer.checking = false;
          if (!c.regionTiles && !sessions.size) {
            layer.wet = false;
            scheduleExport(layer);
          }
        }).catch(() => { layer.checking = false; });
      }
    }
    if (anyWet) {
      request();
      raf = requestAnimationFrame(tick);
    } else {
      lastTick = 0;
    }
  };
  const wake = () => {
    if (!raf && !destroyed) raf = requestAnimationFrame(tick);
  };

  // ---------------------------------------------------------------- persistence cache
  // exportLayerState must be synchronous, so each layer keeps an encoded copy.
  // Refreshing it means a full readback plus a sparse scan and deflate, so it
  // only runs once painting has paused (IDLE_BEFORE_SAVE_MS without strokes),
  // at most every WET_SAVE_INTERVAL_MS while paint is still wet, and the
  // encoding runs in a worker. Painting never waits for it.
  const IDLE_BEFORE_SAVE_MS = 1500;
  const WET_SAVE_INTERVAL_MS = 20000;
  let lastPaintActivity = 0;
  let encodeWorker = null;
  let encodeSeq = 0;
  const encodeCallbacks = new Map();
  const encodeOffThread = async (payload, transfer) => {
    if (!encodeWorker) {
      const { createLayerStateWorker } = await import('./layerStateWorkerClient.js');
      encodeWorker = createLayerStateWorker();
      encodeWorker.onmessage = (event) => {
        const cb = encodeCallbacks.get(event.data.id);
        encodeCallbacks.delete(event.data.id);
        if (!cb) return;
        if (event.data.error) cb.reject(new Error(event.data.error));
        else cb.resolve(event.data.state);
      };
    }
    return new Promise((resolve, reject) => {
      const id = ++encodeSeq;
      encodeCallbacks.set(id, { resolve, reject });
      encodeWorker.postMessage({ ...payload, id }, transfer);
    });
  };
  const runExport = async (layer) => {
    if (destroyed || !layers.has(layer.id) || layer.exporting) return;
    layer.exporting = true;
    try {
      const rb = await layer.engine.readback();
      const S = rb.fields.water.buffer;
      const Pbuf = rb.wetPig[0].buffer;
      const R = rb.dryRefl.buffer;
      const meta = {
        width: simWidth,
        height: simHeight,
        slots: layer.engine.state.slots.map((p) => (p ? { id: p.id, code: p.code, target: p.target } : null)),
        substrate: Array.from(layer.engine.substrate()),
        surface: surfaceSettings,
        docWidth,
        docHeight,
      };
      layer.cache = await encodeOffThread({ S: new Float32Array(S), P: new Float32Array(Pbuf), R: new Float32Array(R), n: simWidth * simHeight, meta }, [S, Pbuf, R]);
      layer.cacheDirty = false;
      layer.lastExport = performance.now();
    } catch (error) {
      layer.cacheError = String(error && error.message ? error.message : error);
    } finally {
      layer.exporting = false;
    }
  };
  const scheduleExport = (layer, delay = IDLE_BEFORE_SAVE_MS) => {
    layer.cacheDirty = true;
    clearTimeout(layer.exportTimer);
    layer.exportTimer = setTimeout(function attempt() {
      if (destroyed || !layers.has(layer.id)) return;
      const quietFor = performance.now() - lastPaintActivity;
      if (sessions.size || quietFor < IDLE_BEFORE_SAVE_MS) {
        layer.exportTimer = setTimeout(attempt, IDLE_BEFORE_SAVE_MS - Math.min(quietFor, IDLE_BEFORE_SAVE_MS) + 50);
        return;
      }
      const idle = typeof requestIdleCallback === 'function' ? requestIdleCallback : (fn) => setTimeout(fn, 0);
      idle(() => {
        runExport(layer).then(() => {
          if (layer.wet && layers.has(layer.id)) scheduleExport(layer, WET_SAVE_INTERVAL_MS);
        });
      });
    }, delay);
  };

  // ---------------------------------------------------------------- strokes
  // Each drawStroke call carries the points of one pointer event. Points with
  // their own timestamp keep it; otherwise times are spread by arc length
  // between the previous call and now, so speed-dependent physics sees the
  // real hand speed rather than many points arriving in the same millisecond.
  const samplesFor = (session, points, settings) => {
    const now = performance.now();
    const input = session.input || (session.input = { travelled: 0, last: null, pointerType: '' });
    const from = Number.isFinite(session.lastTime) ? session.lastTime : now;
    const lengths = [];
    let total = 0;
    let prev = session.last || points[0];
    for (const pt of points) {
      total += Math.hypot(Number(pt.x) - Number(prev.x), Number(pt.y) - Number(prev.y));
      lengths.push(total);
      prev = pt;
    }
    const samples = points.map((pt, i) => {
      const own = Number(pt.time);
      const time = Number.isFinite(own) && own > 0 ? own : from + (now - from) * (total > 0 ? lengths[i] / total : 1);
      return paintSampleFromPoint(pt, settings, input, scale, time);
    });
    session.lastTime = samples.length ? samples[samples.length - 1].time : now;
    return samples;
  };
  // Debug record of the samples the engine received for the latest stroke.
  let lastStrokeRecord = null;

  function beginStroke(layerId, strokeId, settings = {}) {
    const id = String(strokeId || '').trim() || `paint-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    endStroke(id);
    sessions.set(id, { id, layerId: String(layerId), settings, begun: false });
    return id;
  }

  function endStroke(strokeId) {
    const id = String(strokeId || '').trim();
    const session = sessions.get(id);
    if (!session) return false;
    sessions.delete(id);
    paintActivity.active = sessions.size > 0;
    const layer = layers.get(session.layerId);
    if (layer && session.begun) {
      layer.engine.endStroke();
      layer.needsUpload = true;
      scheduleExport(layer);
      request();
    }
    return true;
  }

  function drawStroke(layerId, points, settings = {}, strokeOptions = {}) {
    if (destroyed || !Array.isArray(points) || !points.length) return false;
    const strokeId = String(strokeOptions.paintStrokeId || strokeOptions.strokeId || '').trim();
    let session = strokeId ? sessions.get(strokeId) : null;
    if (!session) session = sessions.get(beginStroke(layerId, strokeId, settings));
    const layer = physicalLayer(session.layerId);
    if (!layer) return false;
    const engine = layer.engine;
    const fresh = [];
    for (const pt of points) {
      const prev = fresh.length ? fresh[fresh.length - 1] : session.last;
      if (prev && Number(pt.x) === Number(prev.x) && Number(pt.y) === Number(prev.y)) continue;
      fresh.push(pt);
    }
    if (!fresh.length) return true;
    const samples = samplesFor(session, fresh, settings);
    let start = 0;
    if (!session.begun) {
      // Another stroke on this layer still open (missed pointerup): close it first.
      for (const other of sessions.values()) {
        if (other !== session && other.layerId === session.layerId && other.begun) endStroke(other.id);
      }
      engine.setEnvironment(environmentFromPaintSettings(settings));
      const tool = toolFromPaintSettings(settings, scale, session.id);
      engine.beginStroke(tool, samples[0]);
      lastStrokeRecord = { layerId: session.layerId, tool, scale, samples: [samples[0]] };
      session.begun = true;
      start = 1;
    }
    for (let i = start; i < samples.length; i += 1) {
      engine.strokeTo(samples[i]);
      if (lastStrokeRecord && lastStrokeRecord.samples.length < 20000) lastStrokeRecord.samples.push(samples[i]);
    }
    session.last = fresh[fresh.length - 1];
    layer.containsPaint = true;
    layer.wet = true;
    layer.needsUpload = true;
    layer.cacheDirty = true;
    lastPaintActivity = performance.now();
    paintActivity.active = true;
    paintActivity.lastStroke = lastPaintActivity;
    wake();
    return true;
  }

  // ---------------------------------------------------------------- rendering
  function renderSurface(framebuffer, w, h) {
    const engine = surfaceEngine || (layers.size ? layers.values().next().value.engine : null) || (surfaceEngine = createEngine('__surface'));
    if (!paperTex) paperTex = makeTexture();
    if (paperDirty) {
      renderPass(engine, 2, paperTex);
      paperDirty = false;
    }
    drawQuad(paperTex, null, 1, true, framebuffer, w, h);
    return true;
  }

  function renderLayer(id, framebuffer, w, h, opacity = 1, viewModeOverride = null) {
    const key = String(id || '').trim();
    let drew = false;
    if (legacy && legacy.hasPaint(key)) drew = legacy.renderLayer(key, framebuffer, w, h, opacity, viewModeOverride) || drew;
    const layer = layers.get(key);
    if (!layer || !layer.containsPaint) return drew;
    const view = Number(viewModeOverride) === 2 ? 'height' : Number(viewModeOverride) === 1 ? 'pigment' : light.view;
    if (layer.needsUpload || layer.renderedView !== view) {
      const saved = light.view;
      light.view = view;
      renderPass(layer.engine, 1, layer.colourTex);
      light.view = saved;
      layer.needsUpload = false;
      layer.renderedView = view;
    }
    drawQuad(layer.colourTex, null, opacity, false, framebuffer, w, h);
    return true;
  }

  function setDisplaySettings(settings = {}) {
    const next = { ...light };
    if (Number.isFinite(Number(settings.lightAngle))) next.azimuth = Number(settings.lightAngle);
    if (Number.isFinite(Number(settings.lightElevation))) next.elevation = Number(settings.lightElevation);
    if (Number.isFinite(Number(settings.gloss))) next.specular = 0.2 + (Number(settings.gloss) / 100) * 0.8;
    if (settings.physical && Number.isFinite(Number(settings.physical.impastoDepth))) next.depthScale = Number(settings.physical.impastoDepth);
    if (settings.physical && Number.isFinite(Number(settings.physical.timeScale))) {
      timeScale = Math.max(0, Math.min(8, Number(settings.physical.timeScale)));
      for (const layer of layers.values()) layer.engine.scheduler.timeScale = timeScale;
    }
    if (JSON.stringify(next) !== JSON.stringify(light)) {
      light = next;
      paperDirty = true;
      for (const layer of layers.values()) layer.needsUpload = true;
    }
    if (legacy) legacy.setDisplaySettings(settings);
  }

  // ---------------------------------------------------------------- undo
  const trimSnapshots = () => {
    while (snapshotBytes > SNAPSHOT_BUDGET_BYTES && snapshots.length) {
      const oldest = snapshots.shift();
      if (oldest.gpu && oldest.engine) oldest.engine.destroySnapshot(oldest.gpu);
      snapshotBytes -= oldest.bytes || 0;
      oldest.gpu = null;
      oldest.evicted = true;
    }
  };

  function captureLayerSnapshot(id) {
    const key = String(id || '').trim();
    const snapshot = { kind: 'physical', id: key, legacy: legacy && legacy.hasPaint(key) ? legacy.captureLayerSnapshot(key) : null, gpu: null, bytes: 0, containsPaint: false, wet: false };
    const layer = layers.get(key);
    if (layer && layer.containsPaint) {
      snapshot.engine = layer.engine;
      snapshot.gpu = layer.engine.captureSnapshot();
      snapshot.bytes = snapshot.gpu.bytes;
      snapshot.containsPaint = true;
      snapshot.wet = layer.wet;
      snapshotBytes += snapshot.bytes;
      snapshots.push(snapshot);
      trimSnapshots();
    }
    return snapshot;
  }

  function restoreLayerSnapshot(id, snapshot) {
    if (!snapshot || snapshot.kind !== 'physical') return legacy ? legacy.restoreLayerSnapshot(id, snapshot) : false;
    const key = String(id || '').trim();
    if (snapshot.legacy && ensureLegacy()) legacy.restoreLayerSnapshot(key, snapshot.legacy);
    else if (legacy && legacy.hasPaint(key)) legacy.clearLayer(key);
    if (!snapshot.containsPaint) {
      dropLayer(key);
      request();
      return true;
    }
    if (snapshot.evicted || !snapshot.gpu) return false;
    const layer = layers.get(key);
    if (!layer || layer.engine !== snapshot.engine) return false;
    layer.engine.restoreSnapshot(snapshot.gpu);
    layer.containsPaint = true;
    layer.wet = true;
    layer.needsUpload = true;
    scheduleExport(layer);
    wake();
    request();
    return true;
  }

  function destroyLayerSnapshot(snapshot) {
    if (!snapshot || snapshot.kind !== 'physical') {
      if (legacy && snapshot) legacy.destroyLayerSnapshot(snapshot);
      return;
    }
    if (snapshot.legacy && legacy) legacy.destroyLayerSnapshot(snapshot.legacy);
    const index = snapshots.indexOf(snapshot);
    if (index >= 0) snapshots.splice(index, 1);
    if (snapshot.gpu && snapshot.engine) {
      snapshot.engine.destroySnapshot(snapshot.gpu);
      snapshotBytes -= snapshot.bytes || 0;
    }
    snapshot.gpu = null;
  }

  // ---------------------------------------------------------------- persistence
  function exportLayerState(id, canvas) {
    const key = String(id || '').trim();
    const layer = layers.get(key);
    const legacyState = legacy && legacy.hasPaint(key) ? legacy.exportLayerState(key, canvas) : null;
    if (!layer || !layer.containsPaint) return legacyState;
    if (layer.cacheDirty && !layer.exportTimer) scheduleExport(layer);
    if (!layer.cache) return legacyState;
    return legacyState ? { ...layer.cache, legacy: legacyState } : layer.cache;
  }

  async function importLayerState(id, state) {
    const key = String(id || '').trim();
    if (!state || typeof state !== 'object') return false;
    if (state.version !== LAYER_STATE_VERSION) {
      return ensureLegacy() ? legacy.importLayerState(key, state) : false;
    }
    if (state.legacy && ensureLegacy()) await legacy.importLayerState(key, state.legacy);
    if (state.surface) setSurfaceSettings(state.surface, { silent: false });
    let cpuState = decodeLayerState(state, surfacePhysics, substrate);
    // Layers saved at another simulation size (for example before Canvas used
    // the Lab's cell size) are resampled, conserving paint per unit area.
    if (state.simWidth !== simWidth || state.simHeight !== simHeight) {
      cpuState = resamplePaintState(cpuState, simWidth, simHeight, surfacePhysics, substrate);
    }
    const layer = physicalLayer(key);
    layer.engine.upload(cpuState);
    layer.containsPaint = true;
    layer.wet = true;
    layer.needsUpload = true;
    layer.cache = state;
    layer.cacheDirty = false;
    lastPaintActivity = performance.now();
    wake();
    request();
    return true;
  }

  // ---------------------------------------------------------------- remaining interface
  function hasPaint(id) {
    if (id != null) {
      const key = String(id || '').trim();
      return Boolean(layers.get(key)?.containsPaint) || Boolean(legacy && legacy.hasPaint(key));
    }
    return Array.from(layers.values()).some((l) => l.containsPaint) || Boolean(legacy && legacy.hasPaint());
  }

  function dryLayer(id) {
    const key = String(id || '').trim();
    const layer = layers.get(key);
    if (legacy && legacy.hasPaint(key)) legacy.dryLayer(key);
    if (!layer || !layer.containsPaint) return false;
    layer.engine.dryAll();
    layer.needsUpload = true;
    scheduleExport(layer);
    request();
    return true;
  }

  function clearLayer(id) {
    const key = String(id || '').trim();
    if (legacy) legacy.clearLayer(key);
    dropLayer(key);
    request();
    return true;
  }

  function deleteLayer(id) {
    const key = String(id || '').trim();
    if (legacy) legacy.deleteLayer(key);
    dropLayer(key);
  }

  function resize(width, height) {
    docWidth = Math.max(1, Math.floor(width));
    docHeight = Math.max(1, Math.floor(height));
    computeSizes();
    rebuildSurfaces();
    // Resizing re-creates physical layers (their paint is cleared); documented limitation.
    for (const key of Array.from(layers.keys())) dropLayer(key);
    if (surfaceEngine) {
      surfaceEngine.destroy();
      surfaceEngine = null;
    }
    paperDirty = true;
    if (legacy) legacy.resize(width, height);
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    window.removeEventListener('vx-paint-surface-set', onSurfaceSet);
    window.removeEventListener('vx-paint-surface-query', onSurfaceQuery);
    if (encodeWorker) encodeWorker.terminate();
    if (window.__vxPhysicalPaint) delete window.__vxPhysicalPaint;
    if (raf) cancelAnimationFrame(raf);
    for (const key of Array.from(layers.keys())) dropLayer(key);
    if (surfaceEngine) surfaceEngine.destroy();
    snapshots.forEach((s) => s.gpu && s.engine && s.engine.destroySnapshot(s.gpu));
    if (paperTex) gl.deleteTexture(paperTex);
    gl.deleteProgram(program);
    gl.deleteBuffer(quad);
    if (legacy) legacy.destroy();
  }

  // ---------------------------------------------------------------- warm-up
  // Compile pipelines, build the paper and create engines (with a snapshot
  // set) for layers as they appear, at idle time, so the first stroke does
  // not allocate or compile anything.
  const idle = (fn) => (typeof requestIdleCallback === 'function' ? requestIdleCallback(fn, { timeout: 1500 }) : setTimeout(fn, 50));
  const prepareLayer = (id) => {
    const key = String(id || '').trim();
    if (!key || layers.has(key) || destroyed) return;
    idle(() => {
      if (destroyed || layers.has(key)) return;
      const layer = physicalLayer(key);
      layer.engine.warmSnapshots();
    });
  };
  idle(() => {
    if (destroyed) return;
    warmPaintPipelines(device, 'rgba8unorm');
    ensureOffscreen();
  });

  // Paper & Surface panel (Canvas side panel) talks to the document surface via events.
  const onSurfaceSet = (event) => { if (event && event.detail) setSurfaceSettings(event.detail); };
  const onSurfaceQuery = () => window.dispatchEvent(new CustomEvent('vx-paint-surface', { detail: { ...surfaceSettings } }));
  window.addEventListener('vx-paint-surface-set', onSurfaceSet);
  window.addEventListener('vx-paint-surface-query', onSurfaceQuery);

  // Developer diagnostics (console): GPU backlog and simulation info.
  window.__vxPhysicalPaint = {
    gpuBacklogMs: () => {
      const t = performance.now();
      return device.queue.onSubmittedWorkDone().then(() => performance.now() - t);
    },
    info: () => ({ docWidth, docHeight, simWidth, simHeight, scale, layers: layers.size, sessions: sessions.size, snapshotBytes }),
    // Samples, tool and scale of the latest stroke (for replaying it through the Lab engine).
    lastStroke: () => lastStrokeRecord,
    // Physical state summary of a layer: totals plus a coarse grid of each field.
    readLayer: async (id, grid = 8) => {
      const layer = layers.get(String(id || '').trim());
      if (!layer) return null;
      const rb = await layer.engine.readback();
      return summariseReadback(rb, simWidth, simHeight, grid);
    },
    layerIds: () => Array.from(layers.keys()),
    // Advances every painted layer by simulated seconds (deterministic tests).
    simulate: (seconds) => {
      for (const layer of layers.values()) {
        if (!layer.containsPaint) continue;
        layer.engine.simulate(seconds);
        layer.needsUpload = true;
      }
      request();
    },
  };

  const legacyCall = (name, fallback) => (...args) => {
    const system = name === 'drawImportedDab' ? ensureLegacy() : legacy;
    return system && typeof system[name] === 'function' ? system[name](...args) : fallback;
  };

  return {
    kind: 'physical',
    beginStroke,
    endStroke,
    drawStroke,
    renderLayer,
    renderSurface,
    captureLayerSnapshot,
    restoreLayerSnapshot,
    destroyLayerSnapshot,
    exportLayerState,
    importLayerState,
    hasPaint,
    dryLayer,
    clearLayer,
    deleteLayer,
    ensureLayer: (id) => {
      prepareLayer(id);
      return legacy ? legacy.ensureLayer(id) : null;
    },
    resize,
    destroy,
    setDisplaySettings,
    getViewMode: () => light.view,
    getDebugMetrics: legacyCall('getDebugMetrics', { pushReach: 0, forwardReach: 0, sideReach: 0, peakHeight: 0 }),
    // Imported (dab) brushes keep their original renderer for now.
    drawImportedDab: legacyCall('drawImportedDab', false),
    // Moving physical paint with the Move tool is not supported yet (legacy paint still moves).
    beginMove: legacyCall('beginMove', false),
    updateMove: legacyCall('updateMove', false),
    endMove: legacyCall('endMove', false),
    // Physical extras used by the Paint Tool UI and tests.
    getSurfaceSettings: () => ({ ...surfaceSettings }),
    // Paper relief for the drawing tools' surface contact (surfaceContact.js):
    // the physics height field and tooth, so Paper Influence and visual-only
    // mode apply to every tool exactly as to paint.
    getToothField: () => ({
      key: `${surfaceKey}|${simWidth}x${simHeight}`,
      heightField: surfacePhysics.heightField,
      width: simWidth,
      height: simHeight,
      tooth: surfacePhysics.preset.tooth ?? 0,
      friction: surfacePhysics.preset.friction ?? surfacePhysics.preset.tooth ?? 0,
      absorbency: surfacePhysics.preset.absorbency ?? 0,
      preset: surfaceSettings.preset,
      textureScale: surfaceSettings.textureScale,
    }),
    setSurfaceSettings,
    // Resolves when GPU work submitted so far (brush stamps, simulation) is done.
    whenIdle: () => device.queue.onSubmittedWorkDone(),
    getSimulationInfo: () => ({ docWidth, docHeight, simWidth, simHeight, scale, layers: layers.size, snapshotBytes, wetLayers: Array.from(layers.values()).filter((l) => l.wet).length }),
    isLayerStateReady: (id) => {
      const layer = layers.get(String(id || '').trim());
      return !layer || !layer.containsPaint || !layer.cacheDirty;
    },
  };
}

// Totals and a coarse grid of the main physical fields (debug comparisons).
export function summariseReadback(rb, width, height, grid = 8) {
  const n = width * height;
  const pigment = new Float32Array(n);
  for (const layer of [rb.wetPig, rb.depPig, rb.bodyPig]) for (const slot of layer) for (let i = 0; i < n; i += 1) pigment[i] += slot[i];
  const fields = { water: rb.fields.water, paperSat: rb.fields.paperSat, bodyVol: rb.fields.bodyVol, dryHeight: rb.fields.dryHeight, pigment };
  const out = { width, height, grid, totals: {}, cells: {} };
  for (const [name, data] of Object.entries(fields)) {
    let total = 0;
    const cells = new Float32Array(grid * grid);
    for (let y = 0; y < height; y += 1) {
      const gy = Math.min(grid - 1, Math.floor((y / height) * grid));
      for (let x = 0; x < width; x += 1) {
        const v = data[y * width + x];
        total += v;
        cells[gy * grid + Math.min(grid - 1, Math.floor((x / width) * grid))] += v;
      }
    }
    out.totals[name] = total;
    out.cells[name] = Array.from(cells);
  }
  return out;
}

export { createPaintState };
